/**
 * REDEX Opportunity Command Center
 * 
 * PRIMARY PURPOSE: "Show me where money is and what to do next"
 * 
 * This is not a feature dump - it's a decision interface.
 * Every element answers: What should I pursue now?
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Snackbar,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  AutoGraph as AutoGraphIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import stratagemService from '../services/stratagemService';
import { attentionEngineService } from '../services/attentionEngineService';
import { leadsService } from '../services/leadsService';

// Types
interface Opportunity {
  id: string;
  title: string;
  type: 'seo' | 'lead' | 'arbitrage' | 'authority' | 'outreach';
  estimatedValue: number;
  confidence: 'high' | 'medium' | 'low';
  timeToImpact: string;
  description: string;
  primaryAction: string;
  status: 'new' | 'in-progress' | 'ready';
}

interface SystemAction {
  id: string;
  action: string;
  timestamp: Date;
  result: string;
  type: 'success' | 'info' | 'warning';
}

// Confidence badge colors
const confidenceColors = {
  high: 'success',
  medium: 'warning',
  low: 'info',
} as const;

// Value formatter
const formatValue = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
  return `$${value}`;
};

const OpportunityCommandCenter: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [recentActions, setRecentActions] = useState<SystemAction[]>([]);
  const [metrics, setMetrics] = useState({
    totalOpportunityValue: 0,
    leadsGenerated: 0,
    pagesOptimized: 0,
    emailsSent: 0,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  
  // Load data
  useEffect(() => {
    loadOpportunities();
  }, []);
  
  const loadOpportunities = async () => {
    setLoading(true);
    try {
      // Fetch from multiple sources and merge into opportunities
      const [dashboard, leads, industries] = await Promise.all([
        stratagemService.getDashboard().catch(() => null),
        leadsService.getLeads({ limit: 10, status: 'new' }).catch(() => ({ leads: [], total: 0 })),
        attentionEngineService.getIndustries().catch(() => []),
      ]);
      
      const opps: Opportunity[] = [];
      
      // Add arbitrage opportunities from Stratagem
      if (dashboard && dashboard.arbitrage && dashboard.arbitrage.activeOpportunities > 0) {
        opps.push({
          id: 'arb-1',
          title: 'Cross-Market Arbitrage Detected',
          type: 'arbitrage',
          estimatedValue: dashboard.arbitrage.totalValue || 15000,
          confidence: 'high',
          timeToImpact: '2-4 weeks',
          description: `${dashboard.arbitrage.activeOpportunities} spillover opportunities across markets`,
          primaryAction: 'Execute Arbitrage',
          status: 'new',
        });
      }
      
      // Add SEO opportunities from Attention Engine
      if (industries.length > 0) {
        const industry = industries[0];
        opps.push({
          id: `seo-${industry.id}`,
          title: `SEO Optimization: ${industry.name}`,
          type: 'seo',
          estimatedValue: 8500,
          confidence: 'high',
          timeToImpact: '30-45 days',
          description: 'Auto-optimize pages, add canonical links, improve internal linking',
          primaryAction: 'Optimize SEO Now',
          status: 'ready',
        });
      }
      
      // Add lead opportunities
      if (leads.leads.length > 0) {
        opps.push({
          id: 'leads-batch',
          title: `${leads.total} New Leads Awaiting Contact`,
          type: 'lead',
          estimatedValue: leads.total * 2500,
          confidence: 'medium',
          timeToImpact: '1-2 weeks',
          description: `Fresh leads ready for outreach. ${leads.leads.slice(0, 3).map(l => l.company || l.name).join(', ')}...`,
          primaryAction: 'Start Outreach',
          status: 'new',
        });
      }
      
      // Add link building opportunity based on acquisitions
      if (dashboard && dashboard.acquisitions && dashboard.acquisitions.targetsIdentified > 0) {
        opps.push({
          id: 'authority-1',
          title: 'Acquisition Targets Identified',
          type: 'authority',
          estimatedValue: 12000,
          confidence: 'medium',
          timeToImpact: '4-8 weeks',
          description: `${dashboard.acquisitions.targetsIdentified} targets identified, ${dashboard.acquisitions.targetsAnalyzing} analyzing`,
          primaryAction: 'Review Targets',
          status: 'ready',
        });
      }
      
      // Sort by value
      opps.sort((a, b) => b.estimatedValue - a.estimatedValue);
      setOpportunities(opps);
      
      // Calculate metrics
      setMetrics({
        totalOpportunityValue: opps.reduce((sum, o) => sum + o.estimatedValue, 0),
        leadsGenerated: leads.total || 0,
        pagesOptimized: dashboard?.verticalDiscovery?.promotedCount || 0,
        emailsSent: 0,
      });
      
      // Mock recent system actions
      setRecentActions([
        { id: '1', action: 'Stratagem Nexus detected 3 new opportunities', timestamp: new Date(), result: 'Success', type: 'success' },
        { id: '2', action: 'Attention Engine optimized 12 pages', timestamp: new Date(Date.now() - 3600000), result: 'Completed', type: 'success' },
        { id: '3', action: 'Canonical links added to 9 URLs', timestamp: new Date(Date.now() - 7200000), result: 'Applied', type: 'info' },
      ]);
      
    } catch (error) {
      console.error('Failed to load opportunities:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load opportunities. Some features may be limited.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Action handlers
  const handlePursueOpportunity = async (opp: Opportunity) => {
    setExecuting(opp.id);
    try {
      switch (opp.type) {
        case 'seo':
          // Navigate to Attention Engine with action
          navigate('/attention-engine', { state: { autoAction: 'optimize' } });
          break;
        case 'lead':
          navigate('/leads');
          break;
        case 'arbitrage':
          // Execute arbitrage
          await stratagemService.detectIntentSpillover('default');
          setSnackbar({
            open: true,
            message: 'Arbitrage opportunities activated! Check Stratagem Nexus for details.',
            severity: 'success',
          });
          break;
        case 'authority':
          navigate('/stratagem-nexus', { state: { tab: 3 } });
          break;
        case 'outreach':
          navigate('/communications');
          break;
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to execute action. Please try again.',
        severity: 'error',
      });
    } finally {
      setExecuting(null);
    }
  };
  
  const handleGenerateOpportunities = async () => {
    setLoading(true);
    try {
      // Trigger opportunity detection
      await stratagemService.detectIntentSpillover('default');
      await loadOpportunities();
      setSnackbar({
        open: true,
        message: 'New opportunities detected and ranked!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to generate opportunities',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Quick action cards
  const QuickActionCard: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
    color: string;
  }> = ({ title, description, icon, onClick, color }) => (
    <Card
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.25)}`,
          borderColor: color,
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Avatar
          sx={{
            bgcolor: alpha(color, 0.1),
            color: color,
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
  
  if (loading && opportunities.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header - What should I do now? */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                Opportunity Command Center
              </Typography>
              <Typography variant="body1" color="text.secondary">
                The system found <strong>{formatValue(metrics.totalOpportunityValue)}</strong> in opportunities.
                Choose which to pursue.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={handleGenerateOpportunities}
              disabled={loading}
            >
              Generate New Opportunities
            </Button>
          </Box>
        </Box>
        
        {/* Metrics Strip */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.success.main, 0.05) }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {formatValue(metrics.totalOpportunityValue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Opportunity Value
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {opportunities.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Opportunities
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {metrics.leadsGenerated}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Leads Generated
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="secondary.main">
                {metrics.pagesOptimized}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pages Optimized
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Grid container spacing={3}>
          {/* Main: Opportunity Queue */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" fontWeight={600}>
                  <StarIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                  Priority Opportunities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ranked by estimated value. Click to pursue.
                </Typography>
              </Box>
              
              <List sx={{ p: 0 }}>
                {opportunities.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No opportunities detected"
                      secondary="Click 'Generate New Opportunities' to scan for revenue opportunities"
                    />
                  </ListItem>
                ) : (
                  opportunities.map((opp, index) => (
                    <React.Fragment key={opp.id}>
                      <ListItem
                        sx={{
                          py: 2,
                          px: 3,
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              fontWeight: 700,
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight={600}>{opp.title}</Typography>
                              <Chip
                                label={opp.confidence.toUpperCase()}
                                size="small"
                                color={confidenceColors[opp.confidence]}
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {opp.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Chip
                                  icon={<MoneyIcon />}
                                  label={formatValue(opp.estimatedValue)}
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                />
                                <Chip
                                  icon={<ScheduleIcon />}
                                  label={opp.timeToImpact}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          }
                        />
                        <Box sx={{ ml: 2, flexShrink: 0 }}>
                          <Button
                            variant="contained"
                            endIcon={executing === opp.id ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
                            onClick={() => handlePursueOpportunity(opp)}
                            disabled={executing === opp.id}
                          >
                            {opp.primaryAction}
                          </Button>
                        </Box>
                      </ListItem>
                      {index < opportunities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </List>
            </Paper>
          </Grid>
          
          {/* Sidebar: System Activity */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <AutoGraphIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                System Activity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What the system has done for you
              </Typography>
              <List dense>
                {recentActions.map((action) => (
                  <ListItem key={action.id} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: alpha(
                            action.type === 'success' ? theme.palette.success.main :
                            action.type === 'warning' ? theme.palette.warning.main :
                            theme.palette.info.main,
                            0.1
                          ),
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 18,
                            color: action.type === 'success' ? 'success.main' :
                                   action.type === 'warning' ? 'warning.main' : 'info.main',
                          }}
                        />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={action.action}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondary={new Date(action.timestamp).toLocaleTimeString()}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
            
            {/* Quick Actions */}
            <Typography variant="h6" fontWeight={600} gutterBottom>
              One-Click Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <QuickActionCard
                  title="Optimize SEO"
                  description="Fix pages now"
                  icon={<SearchIcon />}
                  onClick={() => navigate('/attention-engine')}
                  color={theme.palette.primary.main}
                />
              </Grid>
              <Grid item xs={6}>
                <QuickActionCard
                  title="Generate Leads"
                  description="Find new business"
                  icon={<BusinessIcon />}
                  onClick={() => navigate('/leads')}
                  color={theme.palette.success.main}
                />
              </Grid>
              <Grid item xs={6}>
                <QuickActionCard
                  title="Send Outreach"
                  description="Contact clients"
                  icon={<EmailIcon />}
                  onClick={() => navigate('/communications')}
                  color={theme.palette.info.main}
                />
              </Grid>
              <Grid item xs={6}>
                <QuickActionCard
                  title="View Strategy"
                  description="Stratagem Nexus"
                  icon={<PsychologyIcon />}
                  onClick={() => navigate('/stratagem-nexus')}
                  color={theme.palette.secondary.main}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default OpportunityCommandCenter;
